/**
 * AppTour — Chapter 1 of the guided onboarding, run over the REAL
 * authenticated shell at /home.
 *
 * Activation: /home?tour=app while logged in (direct-URL entry for this
 * checkpoint — permanent product entry points are deferred). Exiting strips
 * the query; "Start Gameplay Tutorial" navigates to /tutorial (Chapter 2).
 *
 * All steps are informational: the spotlight's per-target blockers prevent
 * the highlighted real controls from activating, the dim blocks everything
 * else, and only Continue advances — so the tour can never navigate, start
 * a game, matchmake, message, or modify data.
 *
 * Mobile: the tour opens the REAL navigation drawer (via AppShellContext)
 * for steps whose targets live in it, and closes it for on-page targets and
 * on exit/transition. If the user closes the drawer manually (Escape), the
 * effect re-opens it while a drawer step is active.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppShell } from '../../context/AppShellContext';
import { DS2Modal } from '../ui/DS2Modal';
import { SpotlightOverlay } from './SpotlightOverlay';
import { CoachCard } from './CoachCard';
import { useDomTargetRect } from '../../tutorial/spotlight/domTargets';
import {
  INITIAL_TOUR_PHASE,
  reduceAppTour,
  tourStepsFor,
  tourStepTitle,
  tourStepBody,
  type AppTourEvent,
  type AppTourPhase,
} from '../../tutorial/appTour';
import { useTranslation } from '../../i18n/react';

const DESKTOP_SHELL_QUERY = '(min-width: 768px)';

export function AppTour() {
  // Subscribes this component to language changes, so step copy resolved
  // through tourStepTitle/tourStepBody relabels live mid-tour.
  const { t } = useTranslation('tutorial');
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const { drawerOpen, openDrawer, closeDrawer } = useAppShell();

  const active =
    isLoggedIn &&
    location.pathname === '/home' &&
    new URLSearchParams(location.search).get('tour') === 'app';

  const [phase, setPhase] = useState<AppTourPhase>(INITIAL_TOUR_PHASE);
  const [isMobileShell, setIsMobileShell] = useState<boolean>(
    () => typeof window !== 'undefined' && !window.matchMedia(DESKTOP_SHELL_QUERY).matches,
  );

  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_SHELL_QUERY);
    const onChange = () => setIsMobileShell(!mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Reset the machine whenever the tour (re)starts.
  useEffect(() => {
    if (active) setPhase(INITIAL_TOUR_PHASE);
  }, [active]);

  const steps = tourStepsFor(isMobileShell);
  const step = phase.kind === 'step' ? steps[phase.index] : null;

  const dispatch = useCallback((evt: AppTourEvent) => {
    setPhase((p) => reduceAppTour(p, evt, tourStepsFor(
      !window.matchMedia(DESKTOP_SHELL_QUERY).matches,
    ).length));
  }, []);

  // Drawer choreography (mobile only). Re-runs when the user closes the
  // drawer manually mid-step.
  useEffect(() => {
    if (!active) return;
    const needsDrawer = isMobileShell && step?.inDrawer === true;
    if (needsDrawer && !drawerOpen) openDrawer();
    if (!needsDrawer && drawerOpen) closeDrawer();
  }, [active, isMobileShell, step, drawerOpen, openDrawer, closeDrawer]);

  // Terminal phases: leave the tour cleanly.
  useEffect(() => {
    if (!active || phase.kind !== 'done') return;
    closeDrawer();
    if (phase.next === 'gameplay') {
      navigate('/tutorial');
    } else {
      navigate('/home', { replace: true });
    }
  }, [active, phase, navigate, closeDrawer]);

  const rect = useDomTargetRect(active && step ? step.target : null);

  if (!active || phase.kind === 'done') return null;

  if (phase.kind === 'welcome') {
    return (
      <DS2Modal
        open
        title={t('welcome.title')}
        description={t('welcome.body')}
        actions={[
          { id: 'start', label: t('welcome.start'), variant: 'gold', autoFocus: true },
          { id: 'skip', label: t('welcome.skip'), variant: 'neutral' },
        ]}
        onAction={(id) => dispatch(id === 'start' ? { type: 'start' } : { type: 'skip-chapter' })}
        onDismiss={() => dispatch({ type: 'exit' })}
        compact
      />
    );
  }

  if (phase.kind === 'transition') {
    return (
      <DS2Modal
        open
        title={t('transition.title')}
        description={t('transition.body')}
        actions={[
          { id: 'start', label: t('transition.start'), variant: 'gold', autoFocus: true },
          { id: 'exit', label: t('transition.exit'), variant: 'neutral' },
        ]}
        onAction={(id) => dispatch(id === 'start' ? { type: 'start-gameplay' } : { type: 'exit' })}
        onDismiss={() => dispatch({ type: 'exit' })}
      />
    );
  }

  // Step phase — spotlight the real element (informational: blocked).
  if (!step) return null;
  const cutouts = rect ? [rect] : [];
  return (
    <>
      {cutouts.length > 0 && <SpotlightOverlay cutouts={cutouts} interactive={false} />}
      <CoachCard
        progress={t('progress.chapter1', { current: phase.index + 1, total: steps.length })}
        title={tourStepTitle(step)}
        body={tourStepBody(step)}
        avoid={cutouts}
        isMobileLayout={isMobileShell}
        onContinue={() => dispatch({ type: 'continue' })}
        onBack={() => dispatch({ type: 'back' })}
        onSkipChapter={() => dispatch({ type: 'skip-chapter' })}
        onExit={() => dispatch({ type: 'exit' })}
      />
    </>
  );
}
